/**
 * Daily Report Page - Daily metrics from EOC and EOD data
 *
 * Shows:
 * - Amount of calls today (from MeetingBooking)
 * - Amount of cash collected today (from CloserEOD)
 * - Amount of dials today (from SetterEOD)
 * - Progress bar for EOD completion
 * - Missing EODs table with closers/setters who haven't filled out EOD
 * - AI Sentiment analysis scorecards
 */

import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { useQueryState, parseAsString } from "nuqs";
import { MetricCard, MetricCardSkeleton } from "../components/MetricCard";
import { StatCard, StatCardSkeleton } from "../components/StatCard";
import { useSession } from "../auth/client";
import { useFilters } from "../context/FilterContext";
import { useOfferDashboard } from "../context/OfferDashboardContext";

// Icons
const PhoneIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
  </svg>
);

const DollarIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const DialIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.25 4.5l7.5 7.5-7.5 7.5" />
  </svg>
);

const CheckCircleIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const UserIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
  </svg>
);

const CalendarIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
  </svg>
);

// Formatters
const formatCurrency = (value: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(value);

interface CloserEODRecord {
  closerName: string;
  timestamp: string;
  cashGenerated: number;
  closes: number;
  showedUp: number;
  offersMade: number;
  noShows: number;
  reschedules: number;
  leadflowConfidence?: number;
  wentWell?: string;
  couldImprove?: string;
  cashBlockEffective?: string;
  mrrsUpToDate?: string;
  calendarAdherence?: string;
  needHelp?: string;
  aiSentiment?: "positive" | "neutral" | "negative" | "mixed";
  aiSentimentSummary?: string;
}

interface SetterEODRecord {
  setterName: string;
  dials: number;
  connected: number;
  leadsBooked: number;
  wentWell?: string;
  couldImprove?: string;
  cashBlockEffective?: string;
  calendarAdherence?: string;
  cashUpdated?: string;
  needHelp?: string;
  aiSentiment?: "positive" | "neutral" | "negative" | "mixed";
  aiSentimentSummary?: string;
}

interface AggregateSentiment {
  closerSentiment?: "positive" | "neutral" | "negative" | "mixed" | null;
  closerSummary?: string | null;
  closerCount: number;
  setterSentiment?: "positive" | "neutral" | "negative" | "mixed" | null;
  setterSummary?: string | null;
  setterCount: number;
}

interface DailyReportData {
  date: string;
  aggregateSentiment: AggregateSentiment;
  eoc: {
    totalCalls: number;
    cashCollected: number;
    closedCount: number;
  };
  setter: {
    totalDials: number;
    totalConnected: number;
    totalLeadsBooked: number;
    settersCount: number;
  };
  closer: {
    totalCashGenerated: number;
    totalCloses: number;
    totalShowedUp: number;
    totalOffersMade: number;
    totalNoShows: number;
    totalReschedules: number;
    completedCount: number;
    eodCompletionRate: number;
  };
  eodCompletion: {
    total: number;
    completed: number;
    rate: number;
    closers: {
      total: number;
      completed: number;
      missing: string[];
    };
    setters: {
      total: number;
      completed: number;
      missing: string[];
    };
    missingEODs: string[];
  };
  closerEODRecords: CloserEODRecord[];
  setterEODRecords: SetterEODRecord[];
}

// Sentiment badge component
const SentimentBadge = ({ sentiment, summary, showPending = false }: { sentiment?: string; summary?: string; showPending?: boolean }) => {
  // Show pending state if no sentiment and showPending is true
  if (!sentiment && showPending) {
    return (
      <div className="rounded-lg border p-4 bg-[var(--brand-primary)]/10 text-[var(--brand-primary)] border-[var(--brand-primary)]/30">
        <div className="flex items-center gap-2">
          <svg className="w-5 h-5 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          <span className="font-semibold">AI Sentiment: Calculating...</span>
        </div>
        <p className="text-sm opacity-90 mt-1">Check back after 1 PM EST for the daily sentiment analysis.</p>
      </div>
    );
  }

  if (!sentiment) return null;

  const colors = {
    positive: "bg-emerald-500/20 text-emerald-500 border-emerald-500/30",
    neutral: "bg-gray-500/20 text-gray-400 border-gray-500/30",
    negative: "bg-red-500/20 text-red-500 border-red-500/30",
    mixed: "bg-amber-500/20 text-amber-500 border-amber-500/30",
  };

  const icons = {
    positive: "M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z",
    neutral: "M8 12h.01M12 12h.01M16 12h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z",
    negative: "M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z",
    mixed: "M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z",
  };

  return (
    <div className={`rounded-lg border p-4 ${colors[sentiment as keyof typeof colors] || colors.neutral}`}>
      <div className="flex items-center gap-2 mb-2">
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={icons[sentiment as keyof typeof icons] || icons.neutral} />
        </svg>
        <span className="font-semibold capitalize">{sentiment} Sentiment</span>
      </div>
      {summary && <p className="text-sm opacity-90">{summary}</p>}
    </div>
  );
};

// Individual sentiment item for tooltip
interface IndividualSentiment {
  name: string;
  sentiment?: "positive" | "neutral" | "negative" | "mixed";
}

// Sentiment Scorecard for aggregate team sentiment
const SentimentScorecard = ({
  title,
  sentiment,
  summary,
  count,
  index,
  individuals,
}: {
  title: string;
  sentiment?: "positive" | "neutral" | "negative" | "mixed" | null;
  summary?: string | null;
  count: number;
  index: number;
  individuals?: IndividualSentiment[];
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);

  // Sentiment badge colors (small accent only)
  const sentimentBadgeColors = {
    positive: "bg-emerald-500/20 text-emerald-500",
    neutral: "bg-gray-500/20 text-gray-400",
    negative: "bg-red-500/20 text-red-500",
    mixed: "bg-amber-500/20 text-amber-500",
  };

  const icons = {
    positive: "M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z",
    neutral: "M8 12h.01M12 12h.01M16 12h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z",
    negative: "M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z",
    mixed: "M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z",
    pending: "M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15",
  };

  const isPending = !sentiment;

  // Check if summary is long enough to need expansion
  const needsExpansion = summary && summary.length > 120;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{
        duration: 0.5,
        delay: 0.5 + index * 0.1,
        ease: [0.25, 0.46, 0.45, 0.94],
      }}
      className="relative rounded-2xl border border-border bg-card p-6"
    >
      <div className="flex items-start gap-4">
        {/* Icon */}
        <div className="shrink-0 w-12 h-12 rounded-xl flex items-center justify-center bg-muted">
          <svg
            className={`w-6 h-6 text-muted-foreground ${isPending ? "animate-spin" : ""}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d={isPending ? icons.pending : icons[sentiment as keyof typeof icons]}
            />
          </svg>
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-2">
            <h3 className="font-semibold text-lg">{title}</h3>
            <div className="relative">
              <span
                className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded-full cursor-help"
                onMouseEnter={() => setShowTooltip(true)}
                onMouseLeave={() => setShowTooltip(false)}
              >
                {count} {count === 1 ? "report" : "reports"}
              </span>

              {/* Tooltip */}
              {showTooltip && individuals && individuals.length > 0 && (
                <div className="absolute left-0 top-full mt-2 z-50 min-w-[220px] bg-popover border border-border rounded-lg shadow-xl p-3">
                  <div className="text-xs font-medium text-muted-foreground mb-2">Individual Sentiments</div>
                  <div className="space-y-1.5">
                    {individuals.map((ind, i) => {
                      const badgeColor = ind.sentiment
                        ? sentimentBadgeColors[ind.sentiment]
                        : "bg-gray-500/20 text-gray-400";
                      return (
                        <div key={i} className="flex items-center justify-between gap-2">
                          <span className="text-sm truncate">{ind.name}</span>
                          <span className={`text-xs px-1.5 py-0.5 rounded capitalize shrink-0 ${badgeColor}`}>
                            {ind.sentiment || "pending"}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>

          {isPending ? (
            <>
              <div className="font-medium text-muted-foreground mb-1">Calculating...</div>
              <p className="text-sm text-muted-foreground">
                Check back after 1 PM EST for the daily sentiment analysis.
              </p>
            </>
          ) : (
            <>
              <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-sm font-medium mb-2 ${sentimentBadgeColors[sentiment as keyof typeof sentimentBadgeColors]}`}>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={icons[sentiment as keyof typeof icons]} />
                </svg>
                <span className="capitalize">{sentiment}</span>
              </div>
              {summary && (
                <div>
                  <p className={`text-sm text-muted-foreground ${!isExpanded && needsExpansion ? "line-clamp-2" : ""}`}>
                    {summary}
                  </p>
                  {needsExpansion && (
                    <button
                      onClick={() => setIsExpanded(!isExpanded)}
                      className="text-sm text-[var(--brand-primary)] hover:underline mt-1"
                    >
                      {isExpanded ? "Show less" : "Read more"}
                    </button>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </motion.div>
  );
};

// Modal for EOD details
function EODDetailModal({
  isOpen,
  onClose,
  type,
  closerRecord,
  setterRecord,
}: {
  isOpen: boolean;
  onClose: () => void;
  type: "closer" | "setter";
  closerRecord?: CloserEODRecord;
  setterRecord?: SetterEODRecord;
}) {
  if (!isOpen) return null;

  const DetailRow = ({ label, value }: { label: string; value?: string | number }) => (
    <div className="py-3 border-b border-border last:border-0">
      <div className="text-sm font-medium text-muted-foreground mb-1">{label}</div>
      <div className="text-sm">{value || <span className="text-muted-foreground italic">Not provided</span>}</div>
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="relative z-10 w-full max-w-2xl max-h-[85vh] overflow-y-auto bg-card border border-border rounded-2xl shadow-2xl mx-4"
      >
        {/* Header */}
        <div className="sticky top-0 bg-card border-b border-border p-4 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold">
              {type === "closer" ? closerRecord?.closerName : setterRecord?.setterName}
            </h2>
            <p className="text-sm text-muted-foreground">
              {type === "closer" ? "Closer" : "Setter"} End of Day Report
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-muted transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4">
          {type === "closer" && closerRecord && (
            <>
              {/* Metrics Summary */}
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-muted/50 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-emerald-500">{closerRecord.closes}</div>
                  <div className="text-xs text-muted-foreground">Closes</div>
                </div>
                <div className="bg-muted/50 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-green-500">{formatCurrency(closerRecord.cashGenerated)}</div>
                  <div className="text-xs text-muted-foreground">Cash Generated</div>
                </div>
                <div className="bg-muted/50 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold">{closerRecord.showedUp}</div>
                  <div className="text-xs text-muted-foreground">Showed Up</div>
                </div>
              </div>

              {/* AI Sentiment Analysis */}
              <SentimentBadge
                sentiment={closerRecord.aiSentiment}
                summary={closerRecord.aiSentimentSummary}
                showPending={true}
              />

              {/* Leadflow Confidence */}
              {closerRecord.leadflowConfidence !== undefined && (
                <div className="bg-muted/30 rounded-lg p-4">
                  <div className="text-sm font-medium text-muted-foreground mb-2">Leadflow Quality Confidence</div>
                  <div className="flex items-center gap-3">
                    <div className="text-3xl font-bold">{closerRecord.leadflowConfidence}</div>
                    <div className="text-sm text-muted-foreground">/10</div>
                    <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-[var(--brand-gradient-start)] to-[var(--brand-gradient-end)]"
                        style={{ width: `${(closerRecord.leadflowConfidence / 10) * 100}%` }}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Feedback Fields */}
              <div className="space-y-1">
                <DetailRow label="What went well today?" value={closerRecord.wentWell} />
                <DetailRow label="What could have went better?" value={closerRecord.couldImprove} />
                <DetailRow label="Did you use your cash block time effectively?" value={closerRecord.cashBlockEffective} />
                <DetailRow label="Are all of your MRRs caught up to date?" value={closerRecord.mrrsUpToDate} />
                <DetailRow label="How well did you stick to your calendar today?" value={closerRecord.calendarAdherence} />
                <DetailRow label="Do you need help with something?" value={closerRecord.needHelp} />
              </div>
            </>
          )}

          {type === "setter" && setterRecord && (
            <>
              {/* Metrics Summary */}
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-muted/50 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold">{setterRecord.dials}</div>
                  <div className="text-xs text-muted-foreground">Dials</div>
                </div>
                <div className="bg-muted/50 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold">{setterRecord.connected}</div>
                  <div className="text-xs text-muted-foreground">Connected</div>
                </div>
                <div className="bg-muted/50 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-emerald-500">{setterRecord.leadsBooked}</div>
                  <div className="text-xs text-muted-foreground">Leads Booked</div>
                </div>
              </div>

              {/* AI Sentiment Analysis */}
              <SentimentBadge
                sentiment={setterRecord.aiSentiment}
                summary={setterRecord.aiSentimentSummary}
                showPending={true}
              />

              {/* Feedback Fields */}
              <div className="space-y-1">
                <DetailRow label="What went well today?" value={setterRecord.wentWell} />
                <DetailRow label="What could have went better?" value={setterRecord.couldImprove} />
                <DetailRow label="Did you use your cash block time effectively?" value={setterRecord.cashBlockEffective} />
                <DetailRow label="How well did you stick to your calendar today?" value={setterRecord.calendarAdherence} />
                <DetailRow label="Is your cash fully updated from closers in the dashboard?" value={setterRecord.cashUpdated} />
                <DetailRow label="Do you need help with something?" value={setterRecord.needHelp} />
              </div>
            </>
          )}
        </div>
      </motion.div>
    </div>
  );
}

export function DailyReport() {
  const { data: session } = useSession();
  const { offer } = useFilters();
  const offerDashboard = useOfferDashboard();
  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState<DailyReportData | null>(null);
  const [selectedDate, setSelectedDate] = useQueryState(
    "reportDate",
    parseAsString.withDefault(new Date().toISOString().split("T")[0] ?? "")
  );
  const [selectedCloserRecord, setSelectedCloserRecord] = useState<CloserEODRecord | null>(null);
  const [selectedSetterRecord, setSelectedSetterRecord] = useState<SetterEODRecord | null>(null);

  // Fetch data
  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedDate) params.set("date", selectedDate);
      if (offer && offer !== "all") params.set("offer", offer);
      if (offerDashboard.isEmbeddedMode && offerDashboard.dashboardSlug) {
        params.set("dashboard", offerDashboard.dashboardSlug);
      }

      const response = await fetch(`/api/daily-report?${params.toString()}`);
      const result = await response.json();

      if (result.success) {
        setData(result);
      }
    } catch (error) {
      console.error("Failed to fetch daily report:", error);
    } finally {
      setIsLoading(false);
    }
  }, [selectedDate, offer, offerDashboard.dashboardSlug, offerDashboard.isEmbeddedMode]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Get greeting
  const getGreeting = () => {
    const hour = new Date().getHours();
    const firstName = session?.user?.name?.split(" ")[0] || "";
    const timeGreeting = hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";
    return firstName ? `${timeGreeting}, ${firstName}` : timeGreeting;
  };

  // Format date for display
  const formatDateDisplay = (dateStr: string) => {
    const date = new Date(dateStr + "T12:00:00");
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const selectedDateObj = new Date(dateStr + "T12:00:00");
    selectedDateObj.setHours(0, 0, 0, 0);

    if (selectedDateObj.getTime() === today.getTime()) return "Today";
    if (selectedDateObj.getTime() === yesterday.getTime()) return "Yesterday";
    return date.toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric" });
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        {/* Header Skeleton */}
        <div className="flex items-center justify-between">
          <div className="h-8 w-48 rounded bg-muted animate-pulse" />
          <div className="h-10 w-40 rounded bg-muted animate-pulse" />
        </div>

        {/* Metric Cards Skeleton */}
        <div className="grid gap-6 md:grid-cols-4">
          {[0, 1, 2, 3].map((i) => (
            <MetricCardSkeleton key={i} index={i} />
          ))}
        </div>

        {/* EOD Progress Skeleton */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="relative overflow-hidden bg-card border rounded-2xl p-6"
        >
          <div className="h-4 w-40 rounded bg-muted animate-pulse mb-4" />
          <div className="h-3 rounded-full bg-muted animate-pulse mb-4" />
          <div className="h-4 w-24 rounded bg-muted animate-pulse" />
        </motion.div>

        {/* Sentiment Scorecards Skeleton */}
        <div className="grid gap-6 md:grid-cols-2">
          {[0, 1].map((i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: i * 0.1 }}
              className="relative overflow-hidden bg-card border rounded-2xl p-6"
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-muted animate-pulse" />
                <div className="flex-1">
                  <div className="h-5 w-40 rounded bg-muted animate-pulse mb-2" />
                  <div className="h-4 w-24 rounded bg-muted animate-pulse mb-2" />
                  <div className="h-3 w-full rounded bg-muted animate-pulse" />
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Stats Skeleton */}
        <div className="grid gap-4 md:grid-cols-4">
          {[0, 1, 2, 3].map((i) => (
            <StatCardSkeleton key={i} index={i} />
          ))}
        </div>

        {/* Table Skeleton */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="bg-card rounded-xl border p-6"
        >
          <div className="h-6 w-48 rounded bg-muted animate-pulse mb-4" />
          <div className="space-y-3">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="h-12 rounded bg-muted/50 animate-pulse" />
            ))}
          </div>
        </motion.div>
      </div>
    );
  }

  const completionRate = data?.eodCompletion?.rate || 0;
  const completionColor =
    completionRate >= 80 ? "bg-emerald-500" :
    completionRate >= 50 ? "bg-amber-500" :
    "bg-red-500";

  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: [0.25, 0.46, 0.45, 0.94] }}
        className="flex items-center justify-between"
      >
        <div>
          <motion.h1
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1 }}
            className="text-2xl font-bold tracking-tight"
          >
            {getGreeting()}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.15 }}
            className="text-muted-foreground text-sm mt-1"
          >
            Daily Report - {formatDateDisplay(selectedDate)}
          </motion.p>
        </div>

        {/* Date Picker */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.2 }}
        >
          <input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            className="px-3 py-2 rounded-lg border border-input bg-background focus:outline-none focus:ring-2 focus:ring-ring [&::-webkit-calendar-picker-indicator]:invert [&::-webkit-calendar-picker-indicator]:opacity-70 [&::-webkit-calendar-picker-indicator]:hover:opacity-100"
          />
        </motion.div>
      </motion.div>

      {/* Top Metric Cards */}
      <div className="grid gap-6 md:grid-cols-4">
        <MetricCard
          label="Calls Today"
          value={(data?.eoc?.totalCalls || 0).toString()}
          icon={<PhoneIcon />}
          color="blue"
          data={[]}
          index={0}
        />
        <MetricCard
          label="Cash Collected"
          value={formatCurrency(data?.eoc?.cashCollected || 0)}
          icon={<DollarIcon />}
          color="green"
          data={[]}
          index={1}
        />
        <MetricCard
          label="Dials Today"
          value={(data?.setter?.totalDials || 0).toString()}
          icon={<DialIcon />}
          color="brand"
          data={[]}
          index={2}
        />
        <MetricCard
          label="Closes Today"
          value={(data?.eoc?.closedCount || 0).toString()}
          icon={<CheckCircleIcon />}
          color="green"
          data={[]}
          index={3}
        />
      </div>

      {/* EOD Completion Progress */}
      <motion.div
        initial={{ opacity: 0, y: 20, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{
          duration: 0.5,
          delay: 0.4,
          ease: [0.25, 0.46, 0.45, 0.94],
        }}
        className="relative overflow-hidden bg-card border border-border rounded-2xl p-6"
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">EOD Completion</h3>
          <span className="text-2xl font-bold">{completionRate}%</span>
        </div>

        {/* Progress Bar */}
        <div className="relative h-4 bg-muted rounded-full overflow-hidden mb-4">
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${completionRate}%` }}
            transition={{ duration: 0.8, ease: "easeOut", delay: 0.5 }}
            className={`absolute left-0 top-0 h-full ${completionColor} rounded-full`}
          />
        </div>

        <p className="text-sm text-muted-foreground">
          {data?.eodCompletion?.completed || 0} of {data?.eodCompletion?.total || 0} reps have filled out their EOD
        </p>
      </motion.div>

      {/* Team Sentiment Scorecards */}
      <div className="grid gap-6 md:grid-cols-2">
        <SentimentScorecard
          title="Closer Team Sentiment"
          sentiment={data?.aggregateSentiment?.closerSentiment}
          summary={data?.aggregateSentiment?.closerSummary}
          count={data?.aggregateSentiment?.closerCount || 0}
          index={0}
          individuals={data?.closerEODRecords?.map(r => ({
            name: r.closerName,
            sentiment: r.aiSentiment,
          }))}
        />
        <SentimentScorecard
          title="Setter Team Sentiment"
          sentiment={data?.aggregateSentiment?.setterSentiment}
          summary={data?.aggregateSentiment?.setterSummary}
          count={data?.aggregateSentiment?.setterCount || 0}
          index={1}
          individuals={data?.setterEODRecords?.map(r => ({
            name: r.setterName,
            sentiment: r.aiSentiment,
          }))}
        />
      </div>

      {/* Setter Stats Row */}
      <div className="grid gap-4 md:grid-cols-4">
        <StatCard label="Total Dials" value={(data?.setter?.totalDials || 0).toLocaleString()} index={0} />
        <StatCard label="Connected" value={(data?.setter?.totalConnected || 0).toLocaleString()} index={1} />
        <StatCard label="Leads Booked" value={(data?.setter?.totalLeadsBooked || 0).toLocaleString()} index={2} />
        <StatCard label="Active Setters" value={(data?.setter?.settersCount || 0).toString()} index={3} />
      </div>

      {/* Closer EOD Stats Row */}
      <div className="grid gap-4 md:grid-cols-6">
        <StatCard label="Showed Up" value={(data?.closer?.totalShowedUp || 0).toLocaleString()} index={4} />
        <StatCard label="Offers Made" value={(data?.closer?.totalOffersMade || 0).toLocaleString()} index={5} />
        <StatCard label="Closes" value={(data?.closer?.totalCloses || 0).toLocaleString()} index={6} />
        <StatCard label="Cash (EOD)" value={formatCurrency(data?.closer?.totalCashGenerated || 0)} index={7} />
        <StatCard label="No Shows" value={(data?.closer?.totalNoShows || 0).toLocaleString()} index={8} />
        <StatCard label="Reschedules" value={(data?.closer?.totalReschedules || 0).toLocaleString()} index={9} />
      </div>

      {/* Missing EODs Table */}
      {data?.eodCompletion && (data.eodCompletion.closers?.missing?.length > 0 || data.eodCompletion.setters?.missing?.length > 0) && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.6, ease: [0.25, 0.46, 0.45, 0.94] }}
          className="bg-card rounded-xl border border-border overflow-hidden"
        >
          <div className="p-4 border-b border-border bg-red-500/5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-red-500/10 flex items-center justify-center">
                <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-red-600 dark:text-red-400">Missing EODs</h3>
                <p className="text-sm text-muted-foreground">
                  {(data.eodCompletion.closers?.missing?.length || 0) + (data.eodCompletion.setters?.missing?.length || 0)} team members haven't filled out their EOD
                </p>
              </div>
            </div>
          </div>
          <div className="p-4 space-y-4">
            {/* Missing Closers */}
            {data.eodCompletion.closers?.missing && data.eodCompletion.closers.missing.length > 0 && (
              <div>
                <div className="text-xs font-medium text-muted-foreground mb-2 uppercase tracking-wider">
                  Closers ({data.eodCompletion.closers.missing.length})
                </div>
                <div className="flex flex-wrap gap-2">
                  {data.eodCompletion.closers.missing.map((name, idx) => (
                    <motion.span
                      key={name}
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: 0.7 + idx * 0.05 }}
                      className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-red-500/10 text-red-600 dark:text-red-400 text-sm font-medium"
                    >
                      <UserIcon />
                      {name}
                    </motion.span>
                  ))}
                </div>
              </div>
            )}

            {/* Missing Setters */}
            {data.eodCompletion.setters?.missing && data.eodCompletion.setters.missing.length > 0 && (
              <div>
                <div className="text-xs font-medium text-muted-foreground mb-2 uppercase tracking-wider">
                  Setters ({data.eodCompletion.setters.missing.length})
                </div>
                <div className="flex flex-wrap gap-2">
                  {data.eodCompletion.setters.missing.map((name, idx) => (
                    <motion.span
                      key={name}
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: 0.7 + idx * 0.05 }}
                      className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-amber-500/10 text-amber-600 dark:text-amber-400 text-sm font-medium"
                    >
                      <UserIcon />
                      {name}
                    </motion.span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </motion.div>
      )}

      {/* Completed Closer EODs Table */}
      {data?.closerEODRecords && data.closerEODRecords.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }}
          className="bg-card rounded-xl border border-border overflow-hidden"
        >
          <div className="p-4 border-b border-border">
            <h3 className="text-lg font-semibold">Closer EODs</h3>
            <p className="text-sm text-muted-foreground">Click a row to view full report for {formatDateDisplay(selectedDate)}</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border bg-muted/50">
                  <th className="text-left p-4 font-medium text-muted-foreground">Closer</th>
                  <th className="text-right p-4 font-medium text-muted-foreground">Showed Up</th>
                  <th className="text-right p-4 font-medium text-muted-foreground">Offers Made</th>
                  <th className="text-right p-4 font-medium text-muted-foreground">Closes</th>
                  <th className="text-right p-4 font-medium text-muted-foreground">Cash Generated</th>
                  <th className="text-right p-4 font-medium text-muted-foreground">No Shows</th>
                  <th className="text-right p-4 font-medium text-muted-foreground">Reschedules</th>
                </tr>
              </thead>
              <tbody>
                {data.closerEODRecords.map((record, i) => (
                  <tr
                    key={record.closerName}
                    onClick={() => setSelectedCloserRecord(record)}
                    className={`border-b border-border transition-colors hover:bg-muted/30 cursor-pointer ${i % 2 === 0 ? "" : "bg-muted/20"}`}
                  >
                    <td className="p-4 font-medium">{record.closerName}</td>
                    <td className="p-4 text-right">{record.showedUp}</td>
                    <td className="p-4 text-right">{record.offersMade}</td>
                    <td className="p-4 text-right font-medium text-emerald-600 dark:text-emerald-400">{record.closes}</td>
                    <td className="p-4 text-right font-medium text-green-600 dark:text-green-400">
                      {formatCurrency(record.cashGenerated || 0)}
                    </td>
                    <td className="p-4 text-right text-muted-foreground">{record.noShows}</td>
                    <td className="p-4 text-right text-muted-foreground">{record.reschedules}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-muted/50 font-semibold">
                  <td className="p-4">Total</td>
                  <td className="p-4 text-right">{data.closer.totalShowedUp}</td>
                  <td className="p-4 text-right">{data.closer.totalOffersMade}</td>
                  <td className="p-4 text-right text-emerald-600 dark:text-emerald-400">{data.closer.totalCloses}</td>
                  <td className="p-4 text-right text-green-600 dark:text-green-400">
                    {formatCurrency(data.closer.totalCashGenerated)}
                  </td>
                  <td className="p-4 text-right text-muted-foreground">{data.closer.totalNoShows}</td>
                  <td className="p-4 text-right text-muted-foreground">{data.closer.totalReschedules}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </motion.div>
      )}

      {/* Setter EODs Table */}
      {data?.setterEODRecords && data.setterEODRecords.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.9, ease: [0.25, 0.46, 0.45, 0.94] }}
          className="bg-card rounded-xl border border-border overflow-hidden"
        >
          <div className="p-4 border-b border-border">
            <h3 className="text-lg font-semibold">Setter EODs</h3>
            <p className="text-sm text-muted-foreground">Click a row to view full report for {formatDateDisplay(selectedDate)}</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border bg-muted/50">
                  <th className="text-left p-4 font-medium text-muted-foreground">Setter</th>
                  <th className="text-right p-4 font-medium text-muted-foreground">Dials</th>
                  <th className="text-right p-4 font-medium text-muted-foreground">Connected</th>
                  <th className="text-right p-4 font-medium text-muted-foreground">Leads Booked</th>
                </tr>
              </thead>
              <tbody>
                {data.setterEODRecords.map((record, i) => (
                  <tr
                    key={record.setterName}
                    onClick={() => setSelectedSetterRecord(record)}
                    className={`border-b border-border transition-colors hover:bg-muted/30 cursor-pointer ${i % 2 === 0 ? "" : "bg-muted/20"}`}
                  >
                    <td className="p-4 font-medium">{record.setterName}</td>
                    <td className="p-4 text-right">{record.dials}</td>
                    <td className="p-4 text-right">{record.connected}</td>
                    <td className="p-4 text-right font-medium text-emerald-600 dark:text-emerald-400">{record.leadsBooked}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-muted/50 font-semibold">
                  <td className="p-4">Total</td>
                  <td className="p-4 text-right">{data.setter.totalDials}</td>
                  <td className="p-4 text-right">{data.setter.totalConnected}</td>
                  <td className="p-4 text-right text-emerald-600 dark:text-emerald-400">{data.setter.totalLeadsBooked}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </motion.div>
      )}

      {/* No data state */}
      {(!data?.closerEODRecords || data.closerEODRecords.length === 0) && (!data?.eodCompletion?.missingEODs || data.eodCompletion.missingEODs.length === 0) && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.6 }}
          className="bg-card rounded-xl border border-border p-12 text-center"
        >
          <div className="w-16 h-16 rounded-2xl bg-muted flex items-center justify-center mx-auto mb-4">
            <CalendarIcon />
          </div>
          <h3 className="text-lg font-semibold mb-2">No EOD data for {formatDateDisplay(selectedDate)}</h3>
          <p className="text-muted-foreground">EOD reports will appear here once team members submit their daily reports.</p>
        </motion.div>
      )}

      {/* Closer EOD Detail Modal */}
      <EODDetailModal
        isOpen={!!selectedCloserRecord}
        onClose={() => setSelectedCloserRecord(null)}
        type="closer"
        closerRecord={selectedCloserRecord || undefined}
      />

      {/* Setter EOD Detail Modal */}
      <EODDetailModal
        isOpen={!!selectedSetterRecord}
        onClose={() => setSelectedSetterRecord(null)}
        type="setter"
        setterRecord={selectedSetterRecord || undefined}
      />
    </div>
  );
}
